import { useRef, useState, useEffect } from 'react';
import {
  Box,
  Popover,
  TextField,
  Switch,
  FormControlLabel,
  InputAdornment,
  Button,
} from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useToggle } from 'src/hooks/useToggle';

type Props = {
  yAxisAuto: boolean;
  yAxisFrom: number | null;
  yAxisTo: number | null;
  onChange: (patch: {
    yAxisAuto?: boolean;
    yAxisFrom?: number | null;
    yAxisTo?: number | null;
  }) => void;
};

export const YAxisRangePicker = ({ yAxisAuto, yAxisFrom, yAxisTo, onChange }: Props) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const { state: isOpen, onToggle } = useToggle();

  const [localAuto, setLocalAuto] = useState(yAxisAuto);
  const [localFrom, setLocalFrom] = useState<number | null>(yAxisFrom);
  const [localTo, setLocalTo] = useState<number | null>(yAxisTo);

  // Sync local state when popover opens
  useEffect(() => {
    if (isOpen) {
      setLocalAuto(yAxisAuto);
      setLocalFrom(yAxisFrom);
      setLocalTo(yAxisTo);
    }
  }, [isOpen]); // eslint-disable-line

  const rangeError = !localAuto && localFrom !== null && localTo !== null && localFrom >= localTo;

  const displayValue = yAxisAuto
    ? 'Auto'
    : yAxisFrom !== null && yAxisTo !== null
    ? `${yAxisFrom} – ${yAxisTo}`
    : '';

  const handleClose = () => {
    onToggle();
  };

  const handleConfirm = () => {
    const from = rangeError ? 0 : localFrom;
    const to = rangeError ? 100 : localTo;
    onChange({ yAxisAuto: localAuto, yAxisFrom: from, yAxisTo: to });
    onToggle();
  };

  return (
    <Box sx={{ display: 'flex', flex: '0 0 130px' }}>
      <Box ref={triggerRef}>
        <TextField
          size="small"
          label="Y Axis"
          value={displayValue}
          onClick={onToggle}
          inputProps={{ readOnly: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    display: 'flex',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <ArrowDropDown sx={{ color: 'action.active', fontSize: '1.5rem' }} />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 120, cursor: 'pointer', '& *': { cursor: 'pointer' } }}
        />
      </Box>
      <Popover
        open={isOpen}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localAuto}
                onChange={(e) => {
                  setLocalAuto(e.target.checked);
                  if (e.target.checked) {
                    setLocalFrom(0);
                    setLocalTo(100);
                  }
                }}
              />
            }
            label="Auto Y axis"
          />
          <TextField
            size="small"
            label="Min"
            type="number"
            value={localFrom ?? ''}
            disabled={localAuto}
            error={rangeError}
            onChange={(e) => setLocalFrom(e.target.value !== '' ? Number(e.target.value) : null)}
          />
          <TextField
            size="small"
            label="Max"
            type="number"
            value={localTo ?? ''}
            disabled={localAuto}
            error={rangeError}
            helperText={rangeError ? 'Min value must be less than Max value' : undefined}
            onChange={(e) => setLocalTo(e.target.value !== '' ? Number(e.target.value) : null)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={handleClose}>
              Close
            </Button>
            <Button variant="contained" size="small" disabled={rangeError} onClick={handleConfirm}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
